import { Button } from '@exam-prep/ui';
import type { ReactNode } from 'react';
import { closeApp } from '../../../lib/tma';
import { countdown, type HelpKind } from '../model/flow';
import { ELIGIBILITY, GM, RULES } from '../model/copy';
import type { GmCard, GmLearner, GmMe, StreakProgress } from '../model/types';
import { Panel, StreakTracker } from './GrandMockParts';

/** No Grand Mock is scheduled: just the last results, if any, and the way back. */
export function NoGrandMock({ past, onBack }: { past: ReactNode; onBack: () => void }) {
  return (
    <div className="space-y-4 p-4">
      {past}
      <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
        {GM.back}
      </Button>
    </div>
  );
}

/** A League link opened while mocks are switched off: a gentle line and the way back. */
export function MocksOff({ onBack }: { onBack: () => void }) {
  return <NoGrandMock past={<p role="status">{GM.mocksOff}</p>} onBack={onBack} />;
}

export function PastResults({
  past,
  onResults,
}: {
  past: NonNullable<GmLearner['latestResults']>;
  onResults: (id: string) => void;
}) {
  return (
    <Panel title="Last SSC Rankers League">
      <p>{past.title}: results are out.</p>
      <Button variant="outline" size="lg" className="w-full" onClick={() => onResults(past.id)}>
        See last results
      </Button>
    </Panel>
  );
}

export function TeamNotices({ notices }: { notices: GmCard['notices'] }) {
  if (!notices.length) return null;
  return (
    <Panel title="Updates from the team">
      <ul className="space-y-1.5">
        {notices.slice(-3).map((n) => (
          <li key={`${n.at}-${n.kind}`}>{n.text}</li>
        ))}
      </ul>
    </Panel>
  );
}

export function CancelledStage({ reason }: { reason: string | null | undefined }) {
  return (
    <Panel>
      <p role="status">{GM.cancelled}</p>
      {reason ? <p className="text-muted-foreground">{reason}</p> : null}
    </Panel>
  );
}

export function InProgressStage({ onResume }: { onResume: () => void }) {
  return (
    <Panel>
      <p role="status">Your exam is running. There is no pause; the timer keeps going.</p>
      <Button size="lg" className="w-full" onClick={onResume}>
        Resume the exam
      </Button>
    </Panel>
  );
}

export function SubmittedStage() {
  return (
    <Panel title={GM.submittedTitle}>
      <p role="status">{GM.submittedBody}</p>
      <p className="text-muted-foreground">{GM.resultsPendingBody}</p>
    </Panel>
  );
}

export function ResultsStage({ onResults }: { onResults: () => void }) {
  return (
    <Panel title="Results are out">
      <Button size="lg" className="w-full" onClick={onResults}>
        See results
      </Button>
    </Panel>
  );
}

/** A one-line status (missed, paused). */
export function StatusLine({ text }: { text: string }) {
  return (
    <Panel>
      <p role="status">{text}</p>
    </Panel>
  );
}

const HELP_TEXT: Record<HelpKind, string> = {
  pass: ELIGIBILITY.pass,
  qualified: ELIGIBILITY.qualified,
  streak_today: ELIGIBILITY.streakToday,
  streak_ok: ELIGIBILITY.streakOk,
  streak_broken: ELIGIBILITY.streakBroken,
  needs_pass: ELIGIBILITY.needsPass,
};

export function EligibilityPanel({
  help,
  streak,
  me,
  notEligible,
}: {
  help: HelpKind | null;
  streak: StreakProgress | null;
  me: GmMe | null;
  notEligible: boolean;
}) {
  return (
    <Panel title={ELIGIBILITY.title}>
      <p role="status" className="min-h-10">
        {help ? HELP_TEXT[help] : null}
      </p>
      {streak && (notEligible || !me?.hasPass) ? <StreakTracker streak={streak} /> : null}
      {notEligible ? (
        <>
          <p className="text-xs text-muted-foreground">{ELIGIBILITY.who}</p>
          <Button variant="outline" className="w-full" onClick={closeApp}>
            {ELIGIBILITY.todayCta}
          </Button>
        </>
      ) : null}
    </Panel>
  );
}

export function UpcomingPanel({
  msLeft,
  agreed,
  reminded,
  remindError,
  remindDisabled,
  onRemind,
  onRules,
}: {
  msLeft: number;
  agreed: boolean;
  reminded: boolean;
  remindError: string | null;
  remindDisabled: boolean;
  onRemind: () => void;
  onRules: () => void;
}) {
  return (
    <Panel title={countdown(msLeft)}>
      <p className="text-muted-foreground">
        {agreed ? RULES.agreedAhead : 'You can read and accept the rules ahead of time.'}
      </p>
      {remindError ? (
        <p role="alert" className="text-red-700">
          {remindError}
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        {reminded ? (
          <p role="status" className="rounded-xl border p-3 text-xs">
            {GM.reminded}
          </p>
        ) : (
          <Button variant="outline" className="w-full" disabled={remindDisabled} onClick={onRemind}>
            {GM.remind}
          </Button>
        )}
        {!agreed ? (
          <Button className="w-full" onClick={onRules}>
            {RULES.agreeOnly}
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}

export function ReadyStage({ online, onStart }: { online: boolean; onStart: () => void }) {
  return (
    <Panel>
      <p role="status">
        The SSC Rankers League is open. Tap below to read the instructions and start.
      </p>
      <Button size="lg" className="w-full" disabled={!online} onClick={onStart}>
        {RULES.start}
      </Button>
    </Panel>
  );
}
